/**
 * Role-management page (T055, US7 — FR-016 / FR-019). Admin-only: assign a Workspace user a role
 * (admin/finance/sales/pm) or clear it (read-only). Non-admins see a read-only banner; the assignRole
 * callable enforces admin server-side regardless.
 */
import { useState } from 'react';
import type { SessionUser } from '../services/auth';
import type { Role } from '../types/entities';
import { assignRole } from '../services/roles';

const ROLE_OPTIONS: { value: Role | 'none'; label: string }[] = [
  { value: 'admin', label: 'admin (edit all)' },
  { value: 'finance', label: 'finance (billing)' },
  { value: 'sales', label: 'sales (commercial)' },
  { value: 'pm', label: 'pm (ops/delivery)' },
  { value: 'none', label: 'none (read-only)' },
];

export function Roles({ user }: { user: SessionUser }) {
  const isAdmin = user.role === 'admin';

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role | 'none'>('pm');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await assignRole({ email: email.trim(), role: role === 'none' ? null : role });
      setResult(`${res.email} → ${res.role ?? 'read-only'}. (Takes effect on their next sign-in / token refresh.)`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Assign failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h1>Role management</h1>
      {!isAdmin && (
        <p style={banner}>
          Only admins can assign roles (FR-016). This view is read-only; the server enforces it.
        </p>
      )}

      <label style={row}>
        <span style={lbl}>Workspace email</span>
        <input
          type="email"
          placeholder="user@cosmic-chimps.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ flex: 1 }}
        />
      </label>
      <label style={row}>
        <span style={lbl}>Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value as Role | 'none')}>
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void submit()} disabled={!isAdmin || busy || !email.trim()}>
          {busy ? 'Assigning…' : 'Assign role'}
        </button>
      </div>

      {result && <p style={{ color: 'green' }}>{result}</p>}
      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}
    </section>
  );
}

const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0' };
const lbl: React.CSSProperties = { width: 160 };
const banner: React.CSSProperties = { background: '#fff7e6', border: '1px solid #ffd591', padding: 8, borderRadius: 4 };
